import { useState } from 'react';
import { api } from '../lib/api.ts';
import { ApiError } from '../lib/api.ts';
import { Field, Modal } from './ui.tsx';
import type { CorrectionEntityType, FieldCorrectionDetail } from '../../../shared/types.ts';

/**
 * Envolve um valor exibido em tabela com "sugerir correção": qualquer
 * gerente propõe um valor novo, que fica pendente até um superadmin aprovar.
 * O pedido nunca edita o dado direto — evita que uma correção errada
 * substitua o número sem alguém revisar.
 */
export function CorrectionField({
  entityType,
  entityId,
  field,
  fieldLabel,
  value,
  pending,
  canSuggest,
  onSuggested,
}: {
  entityType: CorrectionEntityType;
  entityId: string;
  field: string;
  fieldLabel: string;
  value: string | null;
  pending?: FieldCorrectionDetail;
  canSuggest: boolean;
  onSuggested: () => void;
}) {
  const [open, setOpen] = useState(false);

  return (
    <span className="correction-field">
      {pending && (
        // Vem antes do valor: numa célula estreita o texto quebra em várias
        // linhas e um selo "solto" depois dele parece pertencer à linha
        // seguinte da tabela — na frente, fica claro que é sobre este campo.
        <span
          className="badge badge-status-warning"
          title={`Sugestão de ${pending.suggestedBy.name}: "${pending.suggestedValue}"`}
        >
          Pendente
        </span>
      )}
      <span>{value ?? '—'}</span>
      {canSuggest && !pending && (
        <button
          type="button"
          className="correction-field__trigger"
          onClick={() => setOpen(true)}
          aria-label={`Sugerir correção para ${fieldLabel}`}
          title="Sugerir correção"
        >
          ✎
        </button>
      )}
      {open && (
        <SuggestCorrectionModal
          entityType={entityType}
          entityId={entityId}
          field={field}
          fieldLabel={fieldLabel}
          currentValue={value}
          onClose={() => setOpen(false)}
          onSaved={() => {
            setOpen(false);
            onSuggested();
          }}
        />
      )}
    </span>
  );
}

function SuggestCorrectionModal({
  entityType,
  entityId,
  field,
  fieldLabel,
  currentValue,
  onClose,
  onSaved,
}: {
  entityType: CorrectionEntityType;
  entityId: string;
  field: string;
  fieldLabel: string;
  currentValue: string | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [suggestedValue, setSuggestedValue] = useState(currentValue ?? '');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function save() {
    if (!suggestedValue.trim()) {
      setError('Informe o valor correto.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await api.post('/corrections', {
        entityType,
        entityId,
        field,
        suggestedValue: suggestedValue.trim(),
        note: note.trim() || null,
      });
      onSaved();
    } catch (err) {
      setError(
        err instanceof ApiError && err.code === 'CORRECTION_PENDING'
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Não foi possível enviar a sugestão.',
      );
      setSaving(false);
    }
  }

  return (
    <Modal
      title={`Sugerir correção — ${fieldLabel}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => void save()}
            disabled={saving}
          >
            {saving ? 'Enviando…' : 'Enviar para aprovação'}
          </button>
        </>
      }
    >
      <div className="stack">
        <p className="text-micro text-muted">
          Valor atual: <strong>{currentValue ?? '—'}</strong>. A mudança só entra em vigor depois
          que um superadmin aprovar.
        </p>
        <Field label="Valor correto">
          <input
            className="input"
            value={suggestedValue}
            onChange={(e) => setSuggestedValue(e.target.value)}
            autoFocus
          />
        </Field>
        <Field label="Por que essa mudança? (opcional)">
          <textarea
            className="textarea"
            rows={2}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Ex.: confirmei com a própria conta."
          />
        </Field>
        {error && (
          <p className="state-error" role="alert">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}
